// SP3c — Composer editor save registry.
//
// The tabbed ComposerEditor unifies the editor's previously-separate saves into
// one "Enregistrer la composition" button. Each editor tab REGISTERS its own
// save (`{ key, order, dirty, save, label }`); the orchestrator (`saveAll`)
// runs only the DIRTY ones, in ascending `order`, awaiting each. A failed save
// is collected (by label) but never aborts the other independent saves.
//
// DS §5: this registry changes only WHEN/TOGETHER each editor's existing
// mutation fires — it never reshapes a payload. Each tab keeps its own state +
// mutation call; the registry just collects "am I dirty / here's my save()".
//
// Two contexts on purpose: the registry api (register/unregister/setDirty) is
// STABLE so a tab's register effect runs once, NOT every time some other tab's
// dirtiness changes — the dirty Set lives in a separate context. (Folding the
// Set into the api made its identity churn and looped the register effect.)
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ComposerSaveKey = 'programs' | 'layout' | 'zone_playlists' | 'background_audio';

export interface RegisterSaveArgs {
  key: ComposerSaveKey;
  /** Fixed orchestration order (programs 1 → layout 2 → zone_playlists 3 → background_audio 4). */
  order: number;
  /** Human label, shown in the partial-failure toast. */
  label: string;
  /** Reactive: true when this tab has unsaved edits. */
  dirty: boolean;
  /** Persists this tab's edits (the tab's existing mutation, payload unchanged). */
  save: () => Promise<void>;
}

interface Entry {
  order: number;
  label: string;
  save: () => Promise<void>;
}

interface RegistryApi {
  register: (key: ComposerSaveKey, entry: Entry) => void;
  unregister: (key: ComposerSaveKey) => void;
  setDirty: (key: ComposerSaveKey, dirty: boolean) => void;
  entriesRef: React.MutableRefObject<Map<ComposerSaveKey, Entry>>;
}

const RegistryCtx = createContext<RegistryApi | null>(null);
const DirtyCtx = createContext<Set<ComposerSaveKey>>(new Set());

export function ComposerSaveProvider({ children }: { children: ReactNode }): JSX.Element {
  const entriesRef = useRef<Map<ComposerSaveKey, Entry>>(new Map());
  const [dirtyKeys, setDirtyKeys] = useState<Set<ComposerSaveKey>>(new Set());

  const register = useCallback((key: ComposerSaveKey, entry: Entry) => {
    entriesRef.current.set(key, entry);
  }, []);

  const unregister = useCallback((key: ComposerSaveKey) => {
    entriesRef.current.delete(key);
    setDirtyKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const setDirty = useCallback((key: ComposerSaveKey, dirty: boolean) => {
    setDirtyKeys((prev) => {
      const has = prev.has(key);
      if (dirty === has) return prev;
      const next = new Set(prev);
      if (dirty) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  // STABLE — no dirtyKeys dependency, so a tab's register effect runs once.
  const api = useMemo<RegistryApi>(
    () => ({ register, unregister, setDirty, entriesRef }),
    [register, unregister, setDirty],
  );

  return (
    <RegistryCtx.Provider value={api}>
      <DirtyCtx.Provider value={dirtyKeys}>{children}</DirtyCtx.Provider>
    </RegistryCtx.Provider>
  );
}

/** A tab calls this to register its save with the orchestrator. */
export function useRegisterSave({ key, order, label, dirty, save }: RegisterSaveArgs): void {
  // Tolerant of no provider: a page that registers a save (ComposerDetailPage /
  // ComposerLayoutEditorPage) can also render standalone (e.g. in unit tests or
  // a deep link before the shell mounts) — there it simply no-ops.
  const api = useContext(RegistryCtx);

  // Always-fresh save() so the registered wrapper closes over the tab's latest
  // state without re-registering on every render.
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!api) return undefined;
    api.register(key, { order, label, save: () => saveRef.current() });
    return () => api.unregister(key);
  }, [api, key, order, label]);

  useEffect(() => {
    if (!api) return;
    api.setDirty(key, dirty);
  }, [api, key, dirty]);
}

/** The shell consumes this for the unified save button + per-tab dirty dots. */
export function useComposerSave(): {
  anyDirty: boolean;
  dirtyKeys: Set<ComposerSaveKey>;
  saveAll: () => Promise<{ ok: boolean; failed: string[] }>;
} {
  const api = useContext(RegistryCtx);
  const dirtyKeys = useContext(DirtyCtx);
  if (!api) throw new Error('useComposerSave must be used within a ComposerSaveProvider');
  const { entriesRef } = api;

  const saveAll = useCallback(async (): Promise<{ ok: boolean; failed: string[] }> => {
    const toRun = [...entriesRef.current.entries()]
      .filter(([k]) => dirtyKeys.has(k))
      .sort((a, b) => a[1].order - b[1].order);
    const failed: string[] = [];
    for (const [, entry] of toRun) {
      try {
        await entry.save();
      } catch {
        failed.push(entry.label);
      }
    }
    return { ok: failed.length === 0, failed };
  }, [dirtyKeys, entriesRef]);

  return { anyDirty: dirtyKeys.size > 0, dirtyKeys, saveAll };
}
